<template>
    <div class="app-activation-wrapper">
        <ActivationInfo v-if="showStep(infoStepName)"
                        :activation-code-min="activationCodeMin"
                        :activation-code-max="activationCodeMax"
                        :next-step="personalDataInputStepName"
                        :other-brand-url="otherBrandUrl"
        >
            <template #txtBeforeActivationMsisdn><slot name="txtActivationInfoBeforeActivationMsisdn"></slot></template>
            <template #txtAfterActivationCode>
                <slot name="txtActivationInfoAfterActivationCode"></slot>
            </template>
            <template #btnNext>
                <slot name="btnActivationInfoNext"></slot>
            </template>
        </ActivationInfo>
        <ActivationPersonalDataInput v-if="showStep(personalDataInputStepName)"
                                     :previous-step="infoStepName"
                                     :next-step="tariffOptionsStepName"
        >
          <template #beforeFormTxt><slot name="personalDataBeforeFormTxt"></slot></template>
          <template #beforeIdentTxt><slot name="personalDataBeforeIdentTxt"></slot></template>
          <template #passwordIndication><slot name="passwordIndicationPersonalData"></slot></template>
          <template #countryInputPostidentLabelTxt><slot name="personalDataCountryInputPostidentLabelTxt"></slot></template>
          <template #countryInputVideoidentLabelTxt><slot name="personalDataCountryInputVideoidentLabelTxt"></slot></template>
          <template #countryInputDataMatchIdentLabelTxt><slot name="personalDataCountryInputDataMatchIdentLabelTxt"></slot></template>
        </ActivationPersonalDataInput>
        <ActivationTariffOptions v-if="showStep(tariffOptionsStepName)"
                                 :extraData="extraData.tariffOptions"
                                 :previous-step="personalDataInputStepName"
                                 :next-step="topUpStepName"
        >
        </ActivationTariffOptions>
        <ActivationTopUp v-if="showStep(topUpStepName)"
                         :credit-card-frame-address="creditCardFrameAddress"
                         :credit-card-redirect-url="creditCardRedirectUrl"
                         :previous-step="tariffOptionsStepName"
                         :next-step="summaryStepName"
                         :monthly-value="topUpMonthlyValue"
        >
          <template #txtBeforeTopUp><slot name="txtTopUpBeforeTopUp"></slot></template>
          <template #txtBeforeAutoTopUpWrapper><slot name="txtTopUpBeforeAutoTopUp"></slot></template>
          <template #lblWithoutAutoWrapper><slot name="lblTopUpWithoutAuto"></slot></template>
          <template #txtHeaderMonthlyAutoTopUpWrapper><slot name="txtTopUpHeaderMonthlyAutoTopUp"></slot></template>
          <template #lblMonthlyAutoTopUpWrapper><slot name="lblTopUpHeaderMonthlyAutoTopUp"></slot></template>
          <template #txtHeaderByLimitAutoTopUpWrapper><slot name="txtTopUpHeaderByLimitAutoTopUp"></slot></template>
          <template #lblByLimitAutoTopUpWrapperFirst><slot name="lblTopUpByLimitAutoTopUpFirst"></slot></template>
          <template #lblByLimitAutoTopUpWrapperSecond><slot name="lblTopUpByLimitAutoTopUpSecond"></slot></template>
          <template #txtPaymentCcInfoBefore><slot name="txtTopUpPaymentCcInfoBefore"></slot></template>
          <template #btnPaymentCcRestart><slot name="btnTopUpPaymentCcRestart"></slot></template>
          <template #lblPaymentBankAccountOwner><slot name="lblTopUpPaymentBankAccountOwner"></slot></template>
          <template #lblPaymentBankAccountIban><slot name="lblTopUpPaymentBankAccountIban"></slot></template>
          <template #txtPaymentBankAccountIbanLiveFeedback><slot name="txtTopUpPaymentBankAccountIbanLiveFeedback"></slot></template>
          <template #lblPaymentBankAccountPaymentConfirmation><slot name="lblTopUpPaymentBankAccountPaymentConfirmation"></slot></template>
          <template #txtPaymentBeforePayment><slot name="txtTopUpPaymentBeforePayment"></slot></template>
          <template #lblPaymentCreditCard><slot name="lblTopUpPaymentCreditCard"></slot></template>
          <template #lblPaymentBankAccount><slot name="lblTopUpPaymentBankAccount"></slot></template>
          <template #btnBack><slot name="btnTopUpBack"></slot></template>
          <template #btnNext><slot name="btnTopUpNext"></slot></template>
        </ActivationTopUp>
        <ActivationSummary v-if="showStep(summaryStepName)"
                           :previous-step="topUpStepName"
                           :next-step="successStepName"
        >
          <template #txtHeader><slot name="txtHeaderSummaryTop"></slot></template>
          <template #txtBeforePersonalDataParent><slot name="txtBeforePersonalDataSummaryTop"></slot></template>
          <template #txtBeforeCreditCardPayment><slot name="txtBeforeCreditCardPaymentSummaryTop"></slot></template>
          <template #txtBeforeBankAccountPayment><slot name="txtBeforeBankAccountPaymentSummaryTop"></slot></template>
          <template #txtAGBParent><slot name="txtAGBSummaryTop"></slot></template>
          <template #txtConsultationConsentParent><slot name="txtConsultationConsentSummaryTop"></slot></template>
          <template #btnNext><slot name="btnNextSummaryTop"></slot></template>
        </ActivationSummary>
        <Success v-if="showStep(successStepName)">
          <template #txtPostIdent><slot name="txtSuccessPostIdent"></slot></template>
          <template #txtVideoIdentPost><slot name="txtSuccessVideoIdentPost"></slot></template>
          <template #txtVideoIdentWeb><slot name="txtSuccessVideoIdentWeb"></slot></template>
        </Success>
    </div>
</template>

<script>
import ActivationInfo, { stepName as infoStepName } from '../Info/ActivationInfo.vue'
import ActivationPersonalDataInput, { stepName as personalDataInputStepName } from '../PersonalData/PersonalDataInput.vue'
import ActivationTariffOptions, { stepName as tariffOptionsStepName } from '../Tariff/TariffOptions.vue'
import ActivationTopUp, { stepName as topUpStepName } from './ActivationAutoTopUpAndPayment.vue'
import ActivationSummary, { stepName as summaryStepName } from './ActivationSummaryWrapper.vue'
import Success, { stepName as successStepName } from '../Success/Success.vue'
import { mapGetters } from 'vuex'
import transaction from '@/mixins/transaction'
import extraData from '@/mixins/extraData'
import processWrapper from '@/mixins/processWrapper'

export default {
  name: 'RossmannActivationWrapper',
  import: {
    infoStepName,
    personalDataInputStepName,
    tariffOptionsStepName,
    topUpStepName,
    summaryStepName,
    successStepName
  },
  data () {
    return {
      topUpMonthlyValue: [2, 19]
    }
  },
  mixins: [
    transaction('activation'),
    extraData(),
    processWrapper('showActivationView')
  ],
  props: {
    creditCardFrameAddress: {
      type: String,
      required: true
    },
    creditCardRedirectUrl: {
      type: String
    },
    otherBrandUrl: {
      type: String
    },
    activationCodeMin: {
      type: Number
    },
    activationCodeMax: {
      type: Number
    }
  },
  computed: {
    ...mapGetters('activation', [
      'showActivationView'
    ])
  },
  components: {
    ActivationTopUp,
    Success,
    ActivationPersonalDataInput,
    ActivationInfo,
    ActivationTariffOptions,
    ActivationSummary
  }
}
</script>
